import { Box, Input, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSelectedChat,
  setSelectedChat,
} from '../../../../../store/apiSlice/chat/chatSlice';
import CustomButton from '../../../../../components/MUIComponents/CustomButton';
import { ButtonTypeEnum } from '../../../../../enums/button-type-enum';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { groupChatSchema } from '../../../../../validation/group-chat-validation';
import { IGroupChatFormTypes } from '../../NewGroupChat/ChatInfoForm';
import { default as CustomInput } from '../../../../../components/MUIComponents/Input';
import {
  IGroupChat,
  useUpdateGroupChatMutation,
} from '../../../../../store/apiSlice/chat/groupApi';
import IconWithBackground from '../../../../../components/MUIComponents/IconWithBackground';
import CameraIcon from '../../../../../components/Icons/CameraIcon';
import useSendMessage from '../../../../../hooks/chat/useSendMessage';
import { IMessageAttributes } from '../../Message/Message';
import { useThemeContext } from '../../../../../theme/ThemeContextProvider';

interface IEditProps {
  onClose: () => void;
}

const Edit = ({ onClose }: IEditProps) => {
  const { theme } = useThemeContext();
  const selectedChat = useSelector(getSelectedChat)! as IGroupChat;
  const [file, setFile] = useState<File | null>(null);
  const [fileObjectUrl, setFileObjectUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [updateGroupChat, { isLoading }] = useUpdateGroupChatMutation();
  const dispatch = useDispatch();
  const sendMessage = useSendMessage();

  useEffect(() => {
    if (!file) {
      setFileObjectUrl('');
      return;
    }

    const url = URL.createObjectURL(file);
    setFileObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, defaultValues, isDirty },
  } = useForm<IGroupChatFormTypes>({
    resolver: yupResolver(groupChatSchema),
    defaultValues: {
      name: selectedChat.name,
      description: selectedChat.description,
    },
    mode: 'onChange',
  });

  const onChangeSubmit: SubmitHandler<IGroupChatFormTypes> = async (data) => {
    try {
      const fd = new FormData();
      if (data.name && data.name !== defaultValues?.name)
        fd.append('name', data.name);
      if (data.description && data.description !== defaultValues?.description)
        fd.append('description', data.description);
      if (file) fd.append('thumbnail', file);

      const updatedChat = await updateGroupChat({
        chatId: selectedChat.id,
        formData: fd,
      }).unwrap();
      dispatch(setSelectedChat(updatedChat));

      if (fd.has('name')) {
        const sendMessageFd = new FormData();

        const attributes: IMessageAttributes = {
          isGroupNameChangedNotification: true,
        };

        sendMessageFd.append('attributes', JSON.stringify(attributes));
        sendMessageFd.append('from', selectedChat.userId);
        sendMessageFd.append('body', fd.get('name')?.toString() || '');

        sendMessage({ chatId: selectedChat.id, formData: sendMessageFd });
      }

      onClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleOpenFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = () => {
    if (fileInputRef.current && fileInputRef.current.files) {
      setFile(fileInputRef.current.files.item(0));
    }
  };

  const thumbSrc =
    selectedChat.thumb &&
    selectedChat.thumb !== 'undefined' &&
    selectedChat.thumb !== 'null'
      ? `data:image/webp;base64, ${selectedChat.thumb}`
      : '';

  const previewSrc = file ? fileObjectUrl : thumbSrc;

  return (
    <>
      <Box
        display={'flex'}
        flexDirection={'column'}
        gap={'1.75rem'}
        width={'100%'}
      >
        <Typography textAlign={'center'} typography={'h2'}>
          Edit group chat
        </Typography>

        <Box
          onClick={handleOpenFileInput}
          alignSelf={'center'}
          sx={{
            '&:hover': { cursor: 'pointer' },
            borderRadius: '50%',
            width: '10rem',
            height: '10rem',
          }}
        >
          <Input
            inputRef={fileInputRef}
            type="file"
            inputProps={{ accept: 'image/png, image/jpeg, image/jpg' }}
            sx={{ display: 'none' }}
            onInput={handleFileChange}
          />
          {previewSrc ? (
            <img
              style={{
                borderRadius: '50%',
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
              src={previewSrc}
              alt=""
            />
          ) : (
            <IconWithBackground
              bgcolor={'background.default'}
              size={'100%'}
              icon={
                <CameraIcon
                  stroke={theme.palette.text.secondary}
                  height={'2.5rem'}
                  width={'2.5rem'}
                />
              }
            />
          )}
        </Box>

        <CustomInput
          register={register('name')}
          variant={'outlined'}
          caption={'Group name:'}
          defaultValue={defaultValues?.name}
          fullWidth
          error={!!errors.name}
          helperText={errors.name ? errors.name.message : ''}
        />
        <CustomInput
          multiline
          rows={5}
          inputProps={{
            style: { overflowY: 'scroll', scrollbarWidth: 'none' },
          }}
          register={register('description')}
          variant={'outlined'}
          caption={'Information:'}
          defaultValue={defaultValues?.description}
          placeholder="Write some information here"
        />
      </Box>
      <Box display={'flex'} gap={'1.375rem'}>
        <CustomButton
          buttonWidth={'210px'}
          variantType={ButtonTypeEnum.TERTIARY}
          sx={{ color: 'text.primary' }}
          onClick={onClose}
          disabled={isLoading}
        >
          cancel
        </CustomButton>
        <CustomButton
          buttonWidth={'210px'}
          onClick={handleSubmit(onChangeSubmit)}
          disabled={!isValid || !(isDirty || file) || isLoading}
        >
          save
        </CustomButton>
      </Box>
    </>
  );
};

export default Edit;
